/**
 * The public page.
 *
 * Centred composition: the chart leads, the headline sits under it, the two
 * actions sit under that. Everything on one axis down the middle.
 *
 * The chart is a real candlestick panel -- price axis, month labels, volume --
 * drawn from the platform's own live SPY data, not a rendered image. Market
 * endpoints are ungated, so it is the same request a signed-in user makes, and
 * a screenshot would be the first dishonest thing a visitor met.
 */
import { formatPrice, type Bar } from "@tt/shared";
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  createChart,
  type IChartApi,
} from "lightweight-charts";
import {
  motion,
  useInView,
  useReducedMotion,
} from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Brand } from "../components/Brand";
import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";

const EASE = [0.22, 0.61, 0.36, 1] as const;

function rise(delay: number) {
  return {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: EASE, delay },
  };
}

function reveal(delay = 0) {
  return {
    initial: { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-70px" },
    transition: { duration: 0.55, ease: EASE, delay },
  };
}

/**
 * A few words each. Anything needing a paragraph does not belong here.
 *
 * The colour is the one the feature wears inside the app -- news is the yellow
 * of a news marker, options the magenta of the chain -- so the page is a
 * legend for the product rather than decoration invented for the page.
 */
const DOES = [
  { k: "Charts", v: "19 timeframes, one second to one month", c: "#5b9dff" },
  { k: "Indicators", v: "44 built in, or write your own Pine", c: "#22c55e" },
  { k: "Options", v: "Full chains, greeks, open interest", c: "#ff45c8" },
  { k: "News", v: "Scored for sentiment and materiality", c: "#ffe14d" },
  { k: "Backtests", v: "Realistic fills, slippage, commission", c: "#3ddbd9" },
  { k: "Signals", v: "Walk-forward and out-of-sample", c: "#a98bff" },
];

const STATS = [
  { figure: "~100%", label: "of daily volume" },
  { figure: "44", label: "indicators" },
  { figure: "19", label: "timeframes" },
  { figure: "7", label: "years of history" },
];

const INDICES = [
  { symbol: "SPY", name: "S&P 500" },
  { symbol: "QQQ", name: "Nasdaq 100" },
  { symbol: "DIA", name: "Dow" },
  { symbol: "IWM", name: "Russell" },
];

export function Landing({ onGetStarted, onSignIn }: {
  onGetStarted: () => void;
  onSignIn: () => void;
}) {
  const quotes = useAsync((s) => api.quotes(INDICES.map((i) => i.symbol), s), []);
  const bars = useAsync((s) => api.bars("SPY", "1d", WINDOW + 420, false, s), []);
  const q = quotes.data?.quotes ?? {};
  const spy = q.SPY;
  const up = (spy?.change_pct ?? 0) >= 0;
  const barList = bars.data?.bars ?? [];
  const lastBar = barList[barList.length - 1] ?? null;

  return (
    <div className="landing">
      <header className="lp-nav">
        <Brand size={22} />
        <nav className="lp-nav-links">
          <a href="#does">Features</a>
          <a href="#figures">Coverage</a>
          <a href="#start">Pricing</a>
        </nav>
        <div className="lp-nav-actions">
          <button className="btn" onClick={onSignIn}>Sign in</button>
          <button className="btn primary" onClick={onGetStarted}>Get started</button>
        </div>
      </header>

      {/* ── hero: chart, then headline, then actions, all on one axis ── */}
      <section className="lp-hero">
        {/* The data wall either side of the panel. Real closes from the same
            bars the chart is drawing, not invented figures -- a decorative
            column of plausible-looking prices on a trading site is exactly the
            kind of small lie that costs trust when someone reads it. */}
        <DataWall bars={barList} />

        <motion.div className="lp-panel" {...rise(0)}>
          <div className="lp-panel-head">
            <span className="lp-sym">SPY</span>
            <span className="lp-tf">1D</span>
            <span className="lp-venue">ZTrading</span>
            <OhlcLegend bar={lastBar} />
            {/* The dot belongs to the price, which is live. The candles are a
                replay of real history, and are labelled as such -- motion that
                implied a streaming tape would be the lie. */}
            <span className="lp-live-tag"><i className="lp-live" />LIVE</span>
            <span className="spacer" />
            <span className="lp-replay">2Y replay</span>
          </div>
          <HeroChart bars={barList} />
        </motion.div>

        <motion.h1 className="lp-title" {...rise(0.1)}>
          Every market.<br />
          <span className="lp-accent">One screen.</span>
        </motion.h1>

        <motion.p className="lp-sub" {...rise(0.17)}>
          Real-time charts, your own Pine scripts, full option chains and
          scored news. Free.
        </motion.p>

        <motion.div className="lp-cta" {...rise(0.23)}>
          <button className="btn primary lp-big" onClick={onGetStarted}>
            Start free
          </button>
          {/* Scrolls to the feature list rather than opening sign-in: a button
              labelled "Learn more" that demands an account is a bait. */}
          <a className="btn lp-big lp-ghost" href="#does">Learn more</a>
        </motion.div>

        <motion.div className="lp-ticker" {...rise(0.3)}>
          {INDICES.map((i) => {
            const quote = q[i.symbol];
            const pct = quote?.change_pct ?? null;
            return (
              <div className="lp-tick" key={i.symbol}>
                <span>{i.name}</span>
                <b>{quote ? formatPrice(quote.price) : "—"}</b>
                <em className={pct === null ? "flat" : pct >= 0 ? "up" : "down"}>
                  {pct === null ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
                </em>
              </div>
            );
          })}
        </motion.div>
      </section>

      <section className="lp-does" id="does">
        {/* The hover transition rides on the gesture object rather than the
            element: a top-level `transition` would replace the reveal's. */}
        {DOES.map((d, i) => (
          <motion.div
            className="lp-do"
            key={d.k}
            style={{ "--c": d.c } as React.CSSProperties}
            {...reveal((i % 3) * 0.07)}
            whileHover={{ y: -3, transition: { type: "spring", stiffness: 420, damping: 30 } }}
          >
            <i className="lp-do-rule" />
            <b>{d.k}</b>
            <span>{d.v}</span>
          </motion.div>
        ))}
      </section>

      <section className="lp-stats" id="figures">
        {STATS.map((st, i) => (
          <motion.div className="lp-stat" key={st.label} {...reveal(i * 0.06)}>
            <strong><CountUp value={st.figure} /></strong>
            <span>{st.label}</span>
          </motion.div>
        ))}
      </section>

      <motion.section className="lp-foot-cta" id="start" {...reveal()}>
        <h2>Start free</h2>
        <p>No card. Your watchlists and scripts stay yours.</p>
        <button className="btn primary lp-big" onClick={onGetStarted}>
          Create an account
        </button>
        {/* Kept, and kept small. Every accuracy number in the product carries
            its sample size and interval; saying so once here is honest without
            making a caveat the first thing anyone reads. */}
        <p className="lp-note">
          No signal here is sold as a sure thing. Every figure the app shows is
          measured out-of-sample, with its sample size and confidence interval
          attached.
        </p>
      </motion.section>

      <footer className="lp-footer">
        <Brand size={16} markOnly />
        <span>ZTrading — research, not advice.</span>
      </footer>
    </div>
  );
}

function CountUp({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const reduced = useReducedMotion();

  // Parsed once. Putting the regex match array in the effect's dependencies
  // restarted the count every frame and it never reached its target.
  const { prefix, target, suffix } = useMemo(() => {
    const m = value.match(/^(\D*)(\d+)(.*)$/);
    return m
      ? { prefix: m[1] ?? "", target: Number(m[2]), suffix: m[3] ?? "" }
      : { prefix: value, target: null as number | null, suffix: "" };
  }, [value]);

  const [shown, setShown] = useState(() =>
    reduced || target === null ? (target ?? 0) : 0,
  );

  useEffect(() => {
    if (!inView || reduced || target === null) return;
    const started = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / 850);
      setShown(Math.round(target * (1 - (1 - t) ** 3)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, reduced, target]);

  if (target === null) return <span ref={ref}>{value}</span>;
  return <span ref={ref}>{prefix}{shown}{suffix}</span>;
}

/**
 * The chart's own OHLC readout, in the shape every terminal uses.
 *
 * The reference this page follows scatters extra labelled quotes across the
 * chart face -- other tickers, other prices. Those are not drawn here: they
 * would have to be invented, and fabricated prices on a trading site are the
 * one decoration that is worth refusing.
 */
function OhlcLegend({ bar }: { bar: Bar | null }) {
  if (!bar) return <span className="lp-loading">loading…</span>;
  const up = bar.close >= bar.open;
  const chg = bar.open === 0 ? 0 : ((bar.close - bar.open) / bar.open) * 100;
  return (
    <span className="lp-ohlc">
      <em>O</em><b className={up ? "up" : "down"}>{bar.open.toFixed(2)}</b>
      <em>H</em><b className={up ? "up" : "down"}>{bar.high.toFixed(2)}</b>
      <em>L</em><b className={up ? "up" : "down"}>{bar.low.toFixed(2)}</b>
      <em>C</em><b className={up ? "up" : "down"}>{bar.close.toFixed(2)}</b>
      <b className={up ? "up" : "down"}>
        {chg >= 0 ? "+" : ""}{chg.toFixed(2)}%
      </b>
      <em className="lp-vol">Vol {(bar.volume / 1e6).toFixed(1)}M</em>
    </span>
  );
}

/** Faint columns of real closes at the edges, behind the panel. */
function DataWall({ bars }: { bars: Bar[] }) {
  const rows = useMemo(() => {
    if (bars.length === 0) return [];
    return bars.slice(-40).map((b) => ({
      t: b.close.toFixed(2),
      up: b.close >= b.open,
    }));
  }, [bars]);
  if (rows.length === 0) return null;
  return (
    <div className="lp-wall" aria-hidden="true">
      <div className="lp-wall-col">
        {rows.map((r, i) => (
          <span key={i} className={r.up ? "up" : "down"}>{r.t}</span>
        ))}
      </div>
      <div className="lp-wall-col">
        {rows.slice().reverse().map((r, i) => (
          <span key={i} className={r.up ? "up" : "down"}>{r.t}</span>
        ))}
      </div>
    </div>
  );
}

/** How many candles are on screen at once; the rest are history to drift through. */
const WINDOW = 130;
const UP = "#22d3ee";
const DOWN = "#f0566a";
/** Milliseconds per candle. Slow enough to read, quick enough to feel alive. */
const STEP_MS = 110;

/**
 * SPY as candles with volume -- the instrument, on display and moving.
 *
 * The window slides forward through two years of daily bars and wraps, so the
 * tape drifts the way a chart does when it is being watched. This is a replay
 * of real history, not a live feed, and the panel says REPLAY for that reason:
 * the price readout beside it is live, the drifting candles are not, and
 * letting the motion imply streaming would be the lie.
 */
function HeroChart({ bars }: { bars: Bar[] }) {
  const host = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const disposed = useRef(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!host.current) return;
    const c = createChart(host.current, {
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#6f7c93",
        fontSize: 11.5,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.028)" },
        horzLines: { color: "rgba(255,255,255,0.028)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.07)",
        // Room underneath for the volume histogram to sit in its own band
        // rather than overprinting the candles.
        scaleMargins: { top: 0.08, bottom: 0.26 },
      },
      timeScale: {
        borderColor: "rgba(255,255,255,0.07)",
        fixLeftEdge: true,
        fixRightEdge: true,
      },
      crosshair: { mode: CrosshairMode.Hidden },
      // A decorative chart that scrolls under the cursor steals the page's
      // scroll and strands the visitor halfway down the hero.
      handleScroll: false,
      handleScale: false,
      autoSize: true,
    });
    chart.current = c;
    disposed.current = false;
    return () => { disposed.current = true; c.remove(); chart.current = null; };
  }, []);

  useEffect(() => {
    const c = chart.current;
    if (!c || bars.length === 0) return;

    // Cyan and salmon rather than the app's green and red. This page follows a
    // reference that uses them, and on a dark navy ground cyan separates from
    // the background far better than green does. Inside the app the price
    // palette stays green/red, which is the convention there.
    const candles = c.addSeries(CandlestickSeries, {
      upColor: UP,
      downColor: DOWN,
      borderUpColor: UP,
      borderDownColor: DOWN,
      wickUpColor: UP,
      wickDownColor: DOWN,
      // The dashed line across the chart with its tag on the axis.
      priceLineVisible: true,
      priceLineWidth: 1,
      priceLineColor: "rgba(255,255,255,0.45)",
      priceLineStyle: 2,
      lastValueVisible: true,
    });
    const vol = c.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
      priceLineVisible: false,
      lastValueVisible: false,
    });
    // Its own scale, pinned to the bottom quarter, so volume never competes
    // with price for the same vertical space.
    c.priceScale("vol").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    const candleAt = (b: Bar) => ({
      time: b.time as never,
      open: b.open, high: b.high, low: b.low, close: b.close,
    });
    const volAt = (b: Bar) => ({
      time: b.time as never,
      value: b.volume,
      color: b.close >= b.open ? `${UP}55` : `${DOWN}55`,
    });

    const draw = (from: number) => {
      const slice = bars.slice(from, from + WINDOW);
      candles.setData(slice.map(candleAt));
      vol.setData(slice.map(volAt));
      c.timeScale().fitContent();
    };

    // Not enough history to drift through, or the visitor asked for less
    // motion: draw it once and leave it alone.
    const spare = bars.length - WINDOW;
    if (reduced || spare <= 1) {
      draw(Math.max(0, spare));
      return () => {
        if (!disposed.current) { c.removeSeries(candles); c.removeSeries(vol); }
      };
    }

    let at = 0;
    let timer = 0;
    draw(at);
    const tick = () => {
      at = (at + 1) % spare;
      draw(at);
      timer = window.setTimeout(tick, STEP_MS);
    };
    timer = window.setTimeout(tick, STEP_MS);

    // A background tab still fires timers, and redrawing a chart nobody can
    // see burns battery for nothing.
    const onVisibility = () => {
      if (document.hidden) window.clearTimeout(timer);
      else timer = window.setTimeout(tick, STEP_MS);
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Cleanups run in declaration order, so the chart above is already gone by
    // the time this runs on unmount.
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      if (!disposed.current) { c.removeSeries(candles); c.removeSeries(vol); }
    };
  }, [bars, reduced]);

  return <div ref={host} className="lp-chart" />;
}
